import React, { useEffect, useState } from 'react';
import {
  Avatar,
  Box,
  Card,
  CardContent,
  CardMedia,
  Chip,
  Container,
  Divider,
  IconButton,
  Skeleton,
  Tooltip,
  Typography,
  useMediaQuery,
  useTheme,
  Button,
} from '@mui/material';
import Grid from '@mui/material/Grid2';
import OpenInNewIcon from '@mui/icons-material/OpenInNew';
import LanguageIcon from '@mui/icons-material/Language';
import InstagramIcon from '@mui/icons-material/Instagram';
import TwitterIcon from '@mui/icons-material/Twitter';
import LaunchIcon from '@mui/icons-material/Launch';
import ArrowUpwardIcon from '@mui/icons-material/ArrowUpward';
import { theme } from '../../../styles/theme';
import { AppBarPadding, BottomPadding, capitalize } from '../../../utils/helperUtils';

// Fallback image (optional): reuse app logo or a placeholder
import fallBackBannerPhoto from '../../../assets/logo_primary.png';

function formatDateTime(dateString) {
  if (!dateString) return null;
  const date = new Date(dateString);
  if (isNaN(date)) return null;
  return new Intl.DateTimeFormat('en-US', {
    dateStyle: 'medium',
    timeStyle: 'short',
  }).format(date);
}

const dummyOrganizer = {
  id: 1,
  name: 'Jasmine Patel',
  avatar: 'https://via.placeholder.com/160',
  description: 'Community-driven organizer creating inclusive food and art events.',
  website: 'https://example.com',
  socials: {
    instagram: 'https://instagram.com/example',
    x: 'https://x.com/example',
  },
  events_count: 4,
};

const dummyEvents = [
  {
    event_listing_id: 14,
    name: 'Taste on Wheels: A Food Truck Fiesta!',
    slug: 'taste-on-wheels-40b18ca3',
    short_description: 'Savor the city’s best eats! A day of food trucks and fresh flavors.',
    banner_photo: 'https://picsum.photos/800/400?random=2',
    status: 'published', // draft | review | published
    address: { city: 'Toronto', state: 'ON' },
    start_time: '2025-06-01T16:00:00Z',
    end_time: '2025-06-01T22:00:00Z',
  },
  {
    event_listing_id: 15,
    name: 'Night Market Under the Stars',
    slug: 'night-market-3cf40047',
    short_description: 'An evening of street food, makers, and live music.',
    banner_photo: '',
    status: 'review',
    address: { city: 'Cleveland', state: 'OH' },
    start_time: '2025-07-12T22:00:00Z',
    end_time: '2025-07-13T02:00:00Z',
  },
  {
    event_listing_id: 16,
    name: 'Flavors of the City',
    slug: 'flavors-of-the-city-a92de2d0',
    short_description: 'Culinary creativity and local culture in one place.',
    banner_photo: 'https://picsum.photos/800/400?random=2',
    status: 'draft',
    address: { city: 'Detroit', state: 'MI' },
    start_time: '2025-08-01T18:00:00Z',
    end_time: '2025-08-01T23:00:00Z',
  },
  {
    event_listing_id: 17,
    name: 'Harvest & Handmade Fair',
    slug: 'harvest-handmade-12d6013b',
    short_description: 'Artisanal goods, small-batch bites, and family fun.',
    banner_photo: 'https://picsum.photos/800/400?random=3',
    status: 'published',
    address: { city: 'Buffalo', state: 'NY' },
    start_time: '2025-09-20T15:00:00Z',
    end_time: '2025-09-20T21:00:00Z',
  },
];

// Status chip palette aligned with EventView conventions
const STATUS = {
  draft: { label: 'DRAFT', color: theme.colors.red, borderColor: theme.colors.red },
  review: { label: 'IN REVIEW', color: theme.colors.secondary, borderColor: theme.colors.secondary },
  published: { label: 'PUBLISHED', color: theme.colors.events.secondary, borderColor: theme.colors.events.primary },
};

function OrganizerView({ organizer = dummyOrganizer, events = dummyEvents }) {
  const muiTheme = useTheme();
  const isMobile = useMediaQuery(muiTheme.breakpoints.down('sm'));

  const [loading, setLoading] = useState(true);

  useEffect(() => {
    // Simulate loading state for skeletons
    const t = setTimeout(() => setLoading(false), 600);
    return () => clearTimeout(t);
  }, []);

  const openInNewTab = (url) => {
    if (!url) return;
    window.open(url, '_blank', 'noopener,noreferrer');
  };

  const initials = organizer?.name
    ? organizer.name
        .split(' ')
        .map((n) => n[0])
        .join('')
        .toUpperCase()
    : 'OR';

  return (
    <Box bgcolor={theme.colors.events.secondary}>
      <AppBarPadding />
      <Container maxWidth="lg" sx={{ px: { xs: 1.5, sm: 2, md: 3 } }}>
        {/* Header / Profile Card */}
        <Card
          elevation={3}
          sx={{
            borderRadius: 4,
            p: { xs: 1.5, sm: 2.5 },
            mt: 1.5,
            mb: 2,
          }}
        >
          <Box display="flex" gap={2} alignItems="center" flexWrap="wrap">
            {loading ? (
              <Skeleton variant="circular" width={56} height={56} />
            ) : organizer?.avatar ? (
              <Avatar src={organizer.avatar} alt={`${organizer.name} avatar`} sx={{ width: { xs: 56, sm: 72 }, height: { xs: 56, sm: 72 } }} />
            ) : (
              <Avatar sx={{ width: { xs: 56, sm: 72 }, height: { xs: 56, sm: 72 }, bgcolor: theme.colors.events.primary }}>{initials}</Avatar>
            )}

            <Box flex={1} minWidth={220}>
              {loading ? (
                <>
                  <Skeleton width={220} height={28} />
                  <Skeleton width={300} height={20} />
                </>
              ) : (
                <>
                  <Typography
                    variant={isMobile ? 'h6' : 'h5'}
                    fontWeight={800}
                    color={theme.colors.events.primaryDark}
                    sx={{
                      display: '-webkit-box',
                      WebkitLineClamp: 2,
                      WebkitBoxOrient: 'vertical',
                      overflow: 'hidden',
                    }}
                  >
                    {organizer?.name || 'Organizer Name'}
                  </Typography>
                  <Typography
                    variant="body2"
                    color="text.secondary"
                    sx={{ mt: 0.5, display: '-webkit-box', WebkitLineClamp: { xs: 3, sm: 'unset' }, WebkitBoxOrient: 'vertical', overflow: 'hidden' }}
                  >
                    {organizer?.description || 'No description available.'}
                  </Typography>
                </>
              )}
            </Box>

            {/* Actions */}
            <Box display="flex" alignItems="center" gap={1}>
              {loading ? (
                <Skeleton variant="rounded" width={120} height={28} />
              ) : (
                <Chip
                  label={`${events?.length || 0} events`}
                  size="small"
                  sx={{
                    bgcolor: theme.colors.events.primary,
                    color: theme.colors.events.secondary,
                    fontWeight: 600,
                    height: 26,
                  }}
                />
              )}

              {!loading && organizer?.website && (
                <Tooltip title="Website" arrow>
                  <IconButton aria-label="Open website" onClick={() => openInNewTab(organizer.website)}>
                    <LanguageIcon />
                  </IconButton>
                </Tooltip>
              )}
              {!loading && organizer?.socials?.instagram && (
                <Tooltip title="Instagram" arrow>
                  <IconButton aria-label="Open Instagram" onClick={() => openInNewTab(organizer.socials.instagram)}>
                    <InstagramIcon />
                  </IconButton>
                </Tooltip>
              )}
              {!loading && organizer?.socials?.x && (
                <Tooltip title="X (Twitter)" arrow>
                  <IconButton aria-label="Open X" onClick={() => openInNewTab(organizer.socials.x)}>
                    <TwitterIcon />
                  </IconButton>
                </Tooltip>
              )}
            </Box>
          </Box>
        </Card>

        {/* Events Section Title */}
        <Box display="flex" alignItems="center" justifyContent="space-between" mb={1} px={0.5}>
          <Typography variant={isMobile ? 'h6' : 'h5'} fontWeight={800} color={theme.colors.events.primaryDark}>
            Events by {organizer?.name || 'Organizer'}
          </Typography>
          <Typography variant="body2" color="text.secondary">{events?.length || 0} total</Typography>
        </Box>
        <Divider sx={{ mb: 2 }} />

        {/* Empty state */}
        {!loading && (!events || events.length === 0) && (
          <Box textAlign="center" py={8}>
            <Typography variant="h6" color="text.secondary">🗓️ No events yet.</Typography>
            <Typography variant="body2" color="text.secondary">When this organizer publishes events, they’ll appear here.</Typography>
          </Box>
        )}

        {/* Events Grid */}
        <Grid container spacing={{ xs: 1.5, sm: 2 }}>
          {(loading ? Array.from({ length: 3 }) : events).map((evt, idx) => (
            <Grid key={loading ? idx : evt.event_listing_id} size={{ xs: 12, sm: 6, md: 4 }}>
              <Card
                variant="outlined"
                sx={{
                  borderRadius: 4,
                  overflow: 'hidden',
                  transition: 'box-shadow 150ms ease',
                  '&:hover': { boxShadow: 3 },
                  position: 'relative',
                  cursor: loading ? 'default' : 'pointer',
                }}
              >
                {loading ? (
                  <Skeleton variant="rectangular" height={{ xs: 140, sm: 160 }} />
                ) : (
                  <>
                    <CardMedia
                      component="img"
                      height={isMobile ? 120 : 140}
                      image={evt?.banner_photo || fallBackBannerPhoto}
                      alt={`${evt?.name || 'Event'} banner`}
                      loading="lazy"
                      sx={{ objectFit: 'cover' }}
                    onClick={() => !loading && openInNewTab(`/event/${evt?.slug}`)}
                    />
                    <Tooltip title="Open event page" arrow>
                      <IconButton
                        aria-label="Open event"
                        sx={{
                          position: 'absolute',
                          top: 8,
                          right: 8,
                          color: 'white',
                          bgcolor: 'rgba(0,0,0,0.5)',
                          '&:hover': {
                            bgcolor: 'rgba(0,0,0,0.7)',
                          },
                        }}
                        onClick={(e) => {
                          e.stopPropagation();
                          openInNewTab(`/event/${evt?.slug}`);
                        }}
                      >
                        <OpenInNewIcon fontSize="small" />
                      </IconButton>
                    </Tooltip>
                  </>
                )}
                <CardContent sx={{ pb: { xs: 0.5, sm: 1 } }}>
                  {loading ? (
                    <>
                      <Skeleton width="80%" height={24} />
                      <Skeleton width="60%" height={18} />
                      <Skeleton width="40%" height={16} />
                    </>
                  ) : (
                    <>
                      <Box display="flex" alignItems="center" justifyContent="space-between" gap={1}>
                        <Typography
                          variant="subtitle1"
                          fontWeight={700}
                          title={evt?.name}
                          sx={{
                            display: '-webkit-box',
                            WebkitLineClamp: 2,
                            WebkitBoxOrient: 'vertical',
                            overflow: 'hidden',
                            minHeight: { xs: 0, sm: 0 },
                          }}
                        >
                          {evt?.name}
                        </Typography>
                        <Chip
                          size="small"
                          label={STATUS[evt?.status]?.label || 'UNKNOWN'}
                          sx={{
                            height: 22,
                            color: STATUS[evt?.status]?.color || theme.colors.events.primaryDark,
                            borderColor: STATUS[evt?.status]?.borderColor || '#ccc',
                            borderWidth: 1,
                            borderStyle: 'solid',
                            bgcolor: ['published', 'paid'].includes(evt?.status)
                              ? STATUS[evt?.status]?.borderColor
                              : 'transparent',
                          }}
                        />
                      </Box>

                      {/* <Typography
                        variant="body2"
                        color="text.secondary"
                        sx={{ mt: 0.5, display: '-webkit-box', WebkitLineClamp: { xs: 2, sm: 2 }, WebkitBoxOrient: 'vertical', overflow: 'hidden' }}
                      >
                        {evt?.short_description || 'No description provided.'}
                      </Typography> */}

                      <Box display="flex" gap={1} mt={1} flexWrap="wrap">
                        <Typography variant="caption" color="text.secondary">
                          {formatDateTime(evt?.start_time)}
                        </Typography>
                        <Typography variant="caption" color="text.secondary">•</Typography>
                        <Typography variant="caption" color="text.secondary">
                          {evt?.address?.city && evt?.address?.state
                            ? `${evt.address.city}, ${evt.address.state}`
                            : 'Location TBA'}
                        </Typography>
                      </Box>
                    </>
                  )}
                </CardContent>
              </Card>
            </Grid>
          ))}
        </Grid>

        {/* CTA to view more (placeholder) */}
        {!loading && events && events.length > 0 && (
          <Box display="flex" justifyContent="center" mt={3}>
            <Button
              variant="outlined"
              onClick={() => window.scrollTo({ top: 0, behavior: 'smooth' })}
              sx={{ borderRadius: 3 }}
            endIcon={<ArrowUpwardIcon />}
            >
              Back to top
            </Button>
          </Box>
        )}
      </Container>
      <BottomPadding />
    </Box>
  );
}

export default OrganizerView;